/**
 * Opening-balance picker (#24315) — a small drawer with an employee +
 * leave-type StyledSelect pair, submitted to open the existing
 * `LeaveAdjustDrawer` with a synthetic zero-target for the chosen pair.
 *
 * Only balance-tracked leave types are offered — the same
 * `BALANCE_TRACKED_LEAVE_TYPES` set (annual/sick/casual) the backend's payout
 * engine and this app's employee-profile panel already treat as canonical.
 * The Record Leave drawer offers the full `useLeaveTypes()` list because a
 * non-balance-tracked type (unpaid / other) can still generate a leave
 * request; here it cannot, because there is no balance to open.
 *
 * ── Loading / error branches ───────────────────────────────────────────────
 *
 * `useLeaveTypes()` runs INSIDE the picker (not in the parent tab): the
 * parent only ever mounts the picker while its drawer is open, so a
 * read-only grant that can never open the drawer never fires the fetch.
 * That satisfies the "only fetch what the picker will consume" review
 * item. The picker mirrors `leave_record_drawer.tsx`'s three-branch shape
 * for the leave-type select (skeleton while loading, an error message with
 * a retry on failure, the real select on ready) — a `[]` from the hook is
 * "loaded, none configured", not "still loading".
 *
 * The employee list is threaded in from `useLeaveRequests().employees` via
 * the parent screen, mirroring how the "Record leave" drawer receives its
 * employees — one control-plane fetch, two consumers. Its load status is
 * threaded alongside so the picker can distinguish "loading" and "error"
 * from a genuine "no employees configured yet" state.
 *
 * The picker is only mounted while its drawer is open (see
 * `leave_balances_tab.tsx`), so it holds no state that could survive a
 * close — no reset-on-close effect is needed.
 *
 * All pure branches live in `leave_opening_balance_utils.ts` so they can
 * be unit-tested React-free; this file is JSX + event wiring only.
 */
import * as React from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Alert,
  Button,
  Drawer,
  StyledSelect,
  type StyledSelectOption,
} from '@jumpstone/ui-react';
import {
  leave_eligible_employees,
  type LeaveEmployeeLookup,
} from '../../data/wagepro_store_leave';
import { useLeaveTypes } from '../../data/use_leave_types';
import { BALANCE_TRACKED_LEAVE_TYPES } from '../../data/wagepro_store_leave_balances';
import {
  isEmployeesGated,
  isLeaveTypesGated,
  resolveEmployeeDisplayName,
  resolveLeaveTypeLabel,
  resolveLeaveTypesStatus,
  type EmployeesStatus,
  type LeaveTypesStatus,
} from './leave_opening_balance_utils';

export type { EmployeesStatus, LeaveTypesStatus };

export interface LeaveOpeningBalanceSelection {
  employee_id: string;
  leave_type: string;
  employee_name: string;
  leave_type_label: string;
}

export interface LeaveOpeningBalancePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employees: readonly LeaveEmployeeLookup[];
  employees_status: EmployeesStatus;
  onContinue: (selection: LeaveOpeningBalanceSelection) => void;
}

const BALANCE_TRACKED_SET: ReadonlySet<string> = new Set(
  BALANCE_TRACKED_LEAVE_TYPES,
);

export function LeaveOpeningBalancePicker({
  open,
  onOpenChange,
  employees,
  employees_status,
  onContinue,
}: LeaveOpeningBalancePickerProps): React.JSX.Element {
  const { t } = useTranslation('wp');
  const { state: leave_types_state, leave_types, retry } = useLeaveTypes();

  const leave_types_status: LeaveTypesStatus = resolveLeaveTypesStatus(
    leave_types_state.status,
  );

  const [employee_id, set_employee_id] = React.useState('');
  const [leave_type, set_leave_type] = React.useState('');
  const [employee_error, set_employee_error] = React.useState<string | null>(
    null,
  );
  const [leave_type_error, set_leave_type_error] = React.useState<
    string | null
  >(null);

  // Contractors accrue no leave (adjustLeaveBalance refuses them).
  const employee_options: StyledSelectOption[] = leave_eligible_employees(
    employees,
  ).map((emp) => ({
    value: emp.id,
    label: emp.full_name,
  }));

  // Restrict to balance-tracked types — an unpaid/other type has no balance
  // to open. The picker still relies on `useLeaveTypes()` (rather than the
  // hardcoded BALANCE_TRACKED_LEAVE_TYPES set) so a config-driven
  // localisation of the leave-type NAMES (e.g. an entity that renamed
  // "Casual" to something jurisdiction-appropriate) is picked up here too.
  const leave_type_options: StyledSelectOption[] = leave_types
    .filter((lt) => lt.tracks_balance && BALANCE_TRACKED_SET.has(lt.code))
    .map((lt) => ({ value: lt.code, label: lt.name }));

  const employees_gated = isEmployeesGated(
    employees_status,
    employee_options.length,
  );
  const leave_types_gated = isLeaveTypesGated(
    leave_types_status,
    leave_type_options.length,
  );

  const employees_empty_ready =
    employees_status === 'ready' && employee_options.length === 0;
  const leave_types_empty_ready =
    leave_types_status === 'ready' && leave_type_options.length === 0;

  function handle_continue(): void {
    let ok = true;
    if (!employee_id) {
      set_employee_error(t('leave.opening_balance_employee_required'));
      ok = false;
    }
    if (!leave_type) {
      set_leave_type_error(t('leave.opening_balance_type_required'));
      ok = false;
    }
    if (!ok) return;
    onContinue({
      employee_id,
      leave_type,
      employee_name: resolveEmployeeDisplayName({
        id: employee_id,
        employees,
      }),
      leave_type_label: resolveLeaveTypeLabel({
        code: leave_type,
        leave_types,
        translate: t,
      }),
    });
  }

  return (
    <Drawer
      open={open}
      onClose={() => onOpenChange(false)}
      title={t('leave.opening_balance_drawer_title')}
    >
      <div className="flex flex-col gap-4 p-1">
        <p className="text-[13px] text-[color:var(--ink-muted)]">
          {t('leave.opening_balance_drawer_description')}
        </p>

        {employees_status === 'error' && (
          <Alert
            variant="danger"
            title={t('leave.opening_balance_employees_error_title')}
          >
            {t('leave.opening_balance_employees_error_description')}
          </Alert>
        )}

        {employees_empty_ready && (
          <Alert
            variant="warning"
            title={t('leave.opening_balance_no_employees_title')}
          >
            {t('leave.opening_balance_no_employees_description')}
          </Alert>
        )}

        {leave_types_empty_ready && (
          <Alert
            variant="warning"
            title={t('leave.opening_balance_no_leave_types_title')}
          >
            {t('leave.opening_balance_no_leave_types_description')}
          </Alert>
        )}

        <label className="flex flex-col gap-1">
          <span className="nv-label">
            {t('leave.opening_balance_employee_label')}
          </span>
          {employees_status === 'loading' ? (
            <div className="nv-skel" style={{ height: 36 }} />
          ) : (
            <StyledSelect
              value={employee_id}
              options={employee_options}
              placeholder={t('leave.opening_balance_employee_placeholder')}
              ariaLabel={t('leave.opening_balance_employee_label')}
              disabled={employees_gated}
              id="leave-opening-balance-employee-select"
              onChange={(next) => {
                set_employee_id(next);
                set_employee_error(null);
              }}
            />
          )}
          {employee_error !== null && (
            <p className="nv-help is-error">{employee_error}</p>
          )}
        </label>

        <label className="flex flex-col gap-1">
          <span className="nv-label">
            {t('leave.opening_balance_type_label')}
          </span>
          {leave_types_status === 'loading' && (
            <div className="nv-skel" style={{ height: 36 }} />
          )}
          {leave_types_status === 'error' && (
            <div className="flex flex-col gap-1">
              <p className="nv-help is-error">
                {t('leave.record_field_leave_type_load_error')}
              </p>
              <Button variant="secondary" size="sm" onClick={retry}>
                {t('leave.retry')}
              </Button>
            </div>
          )}
          {leave_types_status === 'ready' && (
            <StyledSelect
              value={leave_type}
              options={leave_type_options}
              placeholder={t('leave.opening_balance_type_placeholder')}
              ariaLabel={t('leave.opening_balance_type_label')}
              disabled={leave_types_gated}
              id="leave-opening-balance-type-select"
              onChange={(next) => {
                set_leave_type(next);
                set_leave_type_error(null);
              }}
            />
          )}
          {leave_type_error !== null && (
            <p className="nv-help is-error">{leave_type_error}</p>
          )}
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={employees_gated || leave_types_gated}
            data-testid="leave-opening-balance-continue"
            onClick={handle_continue}
          >
            {t('leave.opening_balance_continue')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
